/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */
import React from "react";
import { styled, Switch, Typography, Skeleton } from "@mui/material";
import { StatisticSkeletalLoader } from "../statistic_skeletal_loader";
import { fonts, styles } from "../../themes/dashboard";

const CHART_HEIGHT = 'calc(7 * 42.8px)'

export const TotalSalesContainer = styled('div')({
  backgroundColor: '#ffffff',
  margin: '25px 0px',
  borderRadius: '10px',
  padding: '20px 20px 0px 20px',
  boxShadow: styles.boxShadow,
})

export const StyledSwitch = styled(({ isMobile, ...props}) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme, isMobile }) => ({
  width: 42,
  height: 26,
  padding: 0,
  margin: '10px',
  marginLeft: isMobile ? '0px' : '10px',
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#16a34a',
        opacity: 1,
        border: 0,
        ...theme.applyStyles('dark', {
          backgroundColor: '#2ECA45',
        }),
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.grey[100],
      ...theme.applyStyles('dark', {
        color: theme.palette.grey[600],
      }),
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.7,
      ...theme.applyStyles('dark', {
        opacity: 0.3,
      }),
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: '#E9E9EA',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
    ...theme.applyStyles('dark', {
      backgroundColor: '#39393D',
    }),
  },
}));

export const CardTitle = styled(Typography)({
  fontSize: '16px',
  fontFamily: fonts?.fontFamily,
  fontWeight: '600',
  letterSpacing: '0px',
})

export const StyledTypography = styled(Typography)(({ color, fontSize }) => ({
  fontSize: fontSize,
  fontFamily: fonts?.fontFamily,
  color: color,
  letterSpacing: '0px',
  fontWeight: '400',
}))

export const StyledSpan = styled('span')({
  fontSize: '14px',
  fontFamily: fonts?.fontFamily,
  fontWeight: '500',
})

export const SalesHeaderContainer = styled('div', { 
    shouldForwardProp: (prop) => prop !== 'isMobile' 
  })(({ isMobile }) => ({
    display: 'flex',
    gap: '5px',
    alignItems: isMobile ? 'flex-start' : 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
    flexDirection: isMobile && 'column'
}))

export const SummaryContainer = styled('div', { 
    shouldForwardProp: (prop) => prop !== 'isMobile' 
  })(({ isMobile }) => ({
    marginTop: '20px',
    display: 'flex',
    gap: isMobile ? '0px' : '40px',
    flexDirection: isMobile && 'column'
}))

export const StyledSummaryCard = styled('div', { 
    shouldForwardProp: (prop) => prop !== 'isMobile' 
  })(({ isMobile }) => ({
    width: isMobile ? '100%' : '50%',
}))

export const SummaryDataContainer = styled('div', { 
    shouldForwardProp: (prop) => prop !== 'isMobile' 
  })(({ isMobile }) => ({
    display: 'flex', 
    width: '100%', 
    justifyContent: 'space-between',
    minHeight: isMobile ? '32px' :'38px',
    paddingTop: '10px',
    paddingBottom: '2px',
}))

export const SummaryLoadingContainer = styled('div')({
  width: '200px',
  display: 'flex',
  justifyContent: 'flex-end',
})

// Fixed height reserved on every row (whether or not it renders meta text) so rows with
// a meta line (eg. Total Perqs) stay the same height as plain rows, keeping the two
// summary columns aligned regardless of which row currently has meta data.
export const SummaryMetaData = styled('div')({
  fontSize: '12px',
  fontFamily: fonts?.fontFamily,
  color: '#71717A',
  letterSpacing: '0px',
  fontWeight: '400',
  minHeight: '16px',
  lineHeight: '16px',
  marginTop: '-12px',
  marginBottom: '3px',
  paddingBottom: '2px',
})

export const SummaryMetaLoader = styled(Skeleton)({
  fontSize: '12px',
  width: '55%',
})

export const SummaryData = ({ label, value, metaData, hasMeta, isLoading, isMobile }) => {
  return (
    <React.Fragment>
      <SummaryDataContainer isMobile={isMobile}>
        <StyledTypography fontSize={'14px'}>{label}</StyledTypography>
        {
          isLoading ? (
            <SummaryLoadingContainer>
              <StatisticSkeletalLoader dataTestId={'total-sales-summary-data-loading'} />
            </SummaryLoadingContainer>
          ) : (
            <StyledTypography fontSize={'14px'}>{value}</StyledTypography>
          )
        }
      </SummaryDataContainer>
      <SummaryMetaData data-testid='total-sales-summary-meta-data'>
        {
          isLoading
            ? hasMeta && (
              <SummaryMetaLoader
                variant="text" 
                animation="wave"
                data-testid='total-sales-summary-meta-loading'
              />
            )
            : metaData
        }
      </SummaryMetaData>
    </React.Fragment>
  )
}

export const BlankChartContainer = styled('div')({
  height: CHART_HEIGHT,
})
